import { useEffect, useMemo, useRef, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { Avatar, Badge, BrandLogo, Card, Spinner } from '@tcrd/ui';
import { MapView } from '../../components/map/MapView';
import type { MapEngine, MarkerSpec } from '../../components/map/engine';
import { useCarAnimation } from '../../components/map/useCarAnimation';
import { api, errorMessage } from '../../lib/api';
import { fmtDuration, fmtKm, fmtTime } from '../../lib/format';

interface SharedRide {
  business: string;
  code: string;
  status: string;
  finished: boolean;
  live: boolean;
  driver: { firstName: string; photoUrl: string | null };
  vehicle: { make: string; model: string; color: string; plate: string } | null;
  origin: { address: string; lat: number; lng: number };
  destination: { address: string; lat: number; lng: number };
  route: string | null;
  staleSeconds: number;
  last: { lat: number; lng: number; heading: number | null; recordedAt: string; stale: boolean } | null;
  remainingMeters: number | null;
  etaSeconds: number | null;
}

const HEADLINE: Record<string, string> = {
  CONFIRMED: 'Viaje confirmado',
  DRIVER_ON_THE_WAY: 'El conductor va a recoger',
  DRIVER_ARRIVED: 'El conductor llegó a la recogida',
  IN_PROGRESS: 'En camino al destino',
  COMPLETED: 'Viaje finalizado',
  CANCELLED: 'Viaje cancelado',
  EXPIRED: 'Viaje finalizado',
};

/** Enlace "Compartir mi viaje": lo abre un familiar o amigo, sin cuenta. Se actualiza cada 5 s. */
export function PublicTrackPage() {
  const { token } = useParams<{ token: string }>();
  const [engine, setEngine] = useState<MapEngine | null>(null);
  const q = useQuery({
    queryKey: ['public-track', token],
    queryFn: () => api<SharedRide>(`/public/track/${token}`),
    refetchInterval: (query) => (query.state.data?.finished ? false : 5000),
    retry: 1,
  });
  const car = useCarAnimation(engine, { follow: false });
  const lastAt = useRef<number | null>(null);
  const r = q.data;

  useEffect(() => {
    if (!r) return;
    if (!r.last) {
      car.clear();
      return;
    }
    const at = new Date(r.last.recordedAt).getTime();
    if (lastAt.current !== at) {
      lastAt.current = at;
      car.push({ lat: r.last.lat, lng: r.last.lng, heading: r.last.heading, at }, r.last.stale);
    }
    car.setStale(r.last.stale);
  }, [r, car]);

  const markers = useMemo<MarkerSpec[]>(() => (r ? [{ id: 'o', position: r.origin, kind: 'origin' }, { id: 'd', position: r.destination, kind: 'destination' }] : []), [r]);
  const eta = r?.etaSeconds != null ? fmtTime(new Date(Date.now() + r.etaSeconds * 1000)) : null;

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false}>
        <div className="relative h-full">
          <MapView onEngine={setEngine} className="absolute inset-0" markers={markers} route={r?.route ?? null} fitKey={`${token}-${r?.status ?? ''}`} padding={80} />
          <div className="absolute left-4 top-[max(env(safe-area-inset-top),1rem)] rounded-2xl bg-[var(--surface)] shadow-[var(--shadow-float)] px-3 py-2">
            <BrandLogo className="h-7" />
          </div>
          <div className="absolute inset-x-0 bottom-0 p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
            <Card className="p-5 shadow-[var(--shadow-float)] max-w-xl mx-auto">
              {q.isLoading && <Spinner />}
              {q.error && (
                <div>
                  <h1 className="text-lg font-bold">Enlace no disponible</h1>
                  <p className="text-sm text-[var(--text-muted)]">{errorMessage(q.error)}. Puede que haya caducado; pide uno nuevo a quien te lo envió.</p>
                </div>
              )}
              {r && (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs text-[var(--text-muted)]">Viaje compartido · {r.business}</p>
                      <h1 className="text-xl font-bold">{HEADLINE[r.status] ?? r.status}</h1>
                      {r.live && r.etaSeconds != null && (
                        <p className="text-sm text-[var(--text-muted)]">
                          {r.status === 'IN_PROGRESS' ? 'Llegada' : 'Llega'} en <strong className="text-[var(--text)]">{fmtDuration(r.etaSeconds)}</strong>
                          {eta ? ` · ${eta}` : ''}
                          {r.remainingMeters != null ? ` · ${fmtKm(r.remainingMeters)}` : ''}
                        </p>
                      )}
                    </div>
                    {r.live ? r.last?.stale ? <Badge tone="warning">Sin señal</Badge> : <Badge tone="success">En vivo</Badge> : null}
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <Avatar src={r.driver.photoUrl} firstName={r.driver.firstName} size={48} />
                    <div className="min-w-0">
                      <p className="font-semibold">Conductor: {r.driver.firstName}</p>
                      {r.vehicle && (
                        <p className="text-sm text-[var(--text-muted)] truncate">
                          {r.vehicle.make} {r.vehicle.model} · {r.vehicle.color} · <span className="font-mono font-bold text-[var(--text)]">{r.vehicle.plate}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 text-sm flex flex-col gap-1">
                    <p className="truncate"><span className="text-[var(--text-muted)]">Desde:</span> {r.origin.address}</p>
                    <p className="truncate"><span className="text-[var(--text-muted)]">Hasta:</span> {r.destination.address}</p>
                  </div>
                  <p className="mt-3 text-xs text-[var(--text-muted)] flex items-center gap-1.5">
                    <ShieldCheck className="size-4" aria-hidden /> Viaje {r.code}. La ubicación solo se comparte mientras el viaje está en curso.
                  </p>
                </>
              )}
            </Card>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
